import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Search, Download } from 'lucide-react';

// Vue "Mes chantiers" de l'espace installateur (présentationnel).
//
// UNE liste : ce qui a été fait. Elle répond à « qu'ai-je posé ? », et à rien d'autre.
//
// ⚠️ DEUX LISTES D'ATTENTE ONT ÉTÉ RETIRÉES DE CET ÉCRAN LE 10/09/2026, ET C'EST UN
// REVIREMENT ASSUMÉ. Il faut le dater, parce que le dépôt porte les deux décisions.
//
//   07/09  « même en attente de matériel, l'installateur ou le poseur doit pouvoir
//            retrouver quand même le mandat et l'attestation signées »       (#189)
//   10/09  « Revue pour les installateurs et les poseurs : ils ne doivent pas voir
//            de mandat, ils ne doivent pas voir en attente de signature, ils ne
//            doivent pas voir en attente de matériel. Toutes ces infos remontent
//            dans les alertes pour l'admin opérateur de flexcore. »
//
// C'EST LE 10/09 QUI GAGNE, et ce n'est pas un oubli du 07/09 : c'est un DÉPLACEMENT DE
// CHARGE. Le suivi des dossiers quitte le terrain pour l'admin opérateur, qui le reçoit
// en alertes. Ce qui disparaît ici réapparaît là-bas — et nulle part ailleurs. Cet écran
// ne pose donc AUCUNE alerte : elles ont leur propre lot, seul propriétaire de leur
// contrat.
//
// Ce qui a disparu, exactement : les sections « En attente de signature » et « En
// attente de matériel », le composant `DocumentsDuChantier` qui n'existait que pour
// elles, et le bouton « Mandat signé » qu'il portait. Le mandat est un document
// client-opérateur : le terrain le fait signer, il ne le consulte pas. Le serveur le
// refuse désormais aussi (`mandat:read`, dans `core/authz.py`) — masquer un bouton sans
// fermer la route aurait laissé l'adresse tapée à la main répondre 200.
//
// ⚠️ CE QUI RESTE, ET QU'IL NE FAUT PAS EMPORTER AVEC LE RESTE : le bouton
// « Télécharger l'attestation PDF », plus bas. L'attestation de fin de chantier est LA
// PIÈCE DU POSEUR — il la reçoit déjà par courriel depuis #181 — et elle a été
// explicitement GARDÉE dans l'arbitrage du 10/09. C'est la ligne qu'on perd le plus
// facilement en faisant du retrait en gros.
export default function MesChantiers({ t = (c, s) => s || c, chantiers, chantiersLoading, chantierSearch, setChantierSearch, chantiersFiltered, fmtDate, downloadAttestation }) {
  return (
    <div className="space-y-4 animate-fade-in" data-testid="view-mes-chantiers">
      <h2 className="text-lg font-semibold text-neutral-900">{t('nav_my_chantiers', "Mes chantiers")}</h2>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          className="h-9 pl-8"
          placeholder="Rechercher par client, adresse ou référence chantier..."
          value={chantierSearch}
          onChange={e => setChantierSearch(e.target.value)}
          data-testid="chantiers-search"
        />
      </div>
      {chantiersLoading ? (
        <p className="text-[12px] text-neutral-400 text-center py-8">Chargement...</p>
      ) : chantiersFiltered.length === 0 ? (
        <p className="text-[12px] text-neutral-400 text-center py-8" data-testid="chantiers-empty">{chantiers.length === 0 ? 'Aucun chantier pour l\'instant.' : 'Aucun résultat.'}</p>
      ) : (
        <div className="space-y-2">
          {chantiersFiltered.map(ch => {
            const statutCls = ch.statut_mandat === 'signe' ? 'bg-emerald-50 text-emerald-700' : ch.statut_mandat === 'sans_mandat' ? 'bg-neutral-100 text-neutral-500' : 'bg-amber-50 text-amber-700';
            const statutLbl = ch.statut_mandat === 'signe'
              ? t('status_signed', 'Signé')
              : ch.statut_mandat === 'sans_mandat' ? 'Sans mandat' : t('status_pending', 'En attente');
            return (
              <div key={ch.client_id} className="rounded-lg border border-neutral-200 bg-white p-3" data-testid={`chantier-${ch.client_id}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 text-[13px] truncate">{ch.client_prenom} {ch.client_nom}</p>
                    <p className="text-[11px] text-neutral-500 truncate">{ch.adresse}, {ch.code_postal} {ch.ville}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${statutCls}`}>{statutLbl}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-600 mb-2">
                  <span>📅 {fmtDate(ch.date_installation)}</span>
                  {ch.types_equipements.length > 0 && <span>⚙ {ch.types_equipements.join(', ')}</span>}
                  {/* La chaîne vient du SERVEUR : « DUPONT-260901 · CH-4F2A9C ».
                      Deux écrans qui la recomposeraient chacun de leur côté
                      finiraient par ne plus afficher la même chose. */}
                  {(ch.reference_affichee || ch.reference_chantier) && (
                    <span data-testid={`chantier-reference-${ch.client_id}`}>
                      Réf. {ch.reference_affichee || ch.reference_chantier}
                    </span>
                  )}
                  {/* QUI A POSÉ. La liste montre désormais les chantiers de toute
                      l'équipe : sans ce nom, l'installateur lit huit lignes sans savoir
                      laquelle est de lui. On ne l'affiche donc que quand il y a
                      justement quelque chose à distinguer. */}
                  {ch.de_mon_equipe && ch.pose_par && (
                    <span className="text-neutral-500" data-testid={`chantier-poseur-${ch.client_id}`}>
                      👷 Posé par {ch.pose_par}
                    </span>
                  )}
                </div>
                {/* ⚠️ LA PIÈCE DU POSEUR, ET LA SEULE QUI RESTE. Le retrait du 10/09 a
                    emporté le mandat ; l'attestation est gardée, explicitement. Elle
                    atteste de SON travail et lui arrive déjà par courriel (#181). */}
                {ch.mandat_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] w-full"
                    onClick={() => downloadAttestation(ch.mandat_id, `${ch.client_prenom}_${ch.client_nom}`)}
                    data-testid={`download-att-${ch.client_id}`}
                  >
                    <Download className="w-3 h-3 mr-1" /> Télécharger l'attestation PDF
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
